import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, HostBinding } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FwAlertModule } from '@flywheel-io/vision';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import numbro from 'numbro';
import { Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { acquisitionLabelMaxLength } from '../../app.constants';
import { ConfigService } from '../../config.service';
import { ChoiceDialog, DialogService } from '../../dialogs';
import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { MaterialModule } from '../../material.module';
import { NotificationService } from '../../notification.service';
import { Acquisition } from '../../shared/models/acquisition.model';
import { Container } from '../../shared/models/container.model';
import { Session } from '../../shared/models/session.model';
import { PipesModule } from '../../shared/pipes.module';
import { UploadDropzoneComponent } from '../upload-dropzone/upload-dropzone.component';
import { FileStatus } from '../upload.model';
import { UploadService } from '../upload.service';
import { FwTimeFieldComponent } from '../../shared/components/fw-time-field/fw-time-field.component';

export interface AcquisitionUploadDialogData {
  session: Session;
  acquisition: Acquisition;
}

type AcquisitionUploadDialogResult = Acquisition | null;

@Component({
  host: {
    class: 'mat-dialog-component',
  },
  templateUrl: './acquisition-upload-dialog.component.html',
  styleUrls: [
    '../upload.shared.scss',
    './acquisition-upload-dialog.component.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FwAlertModule,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    UploadDropzoneComponent,
    FwTimeFieldComponent,
  ],
})
export class AcquisitionUploadDialog {
  @HostBinding('attr.test-id') testId = 'acquisition-upload-dialog';

  acquisitionLabelMaxLength = acquisitionLabelMaxLength;
  newAcqForm: FormGroup = this.fb.group({
    label: [null, [Validators.required, Validators.maxLength(acquisitionLabelMaxLength)]],
    timestamp: [`${new Date().getHours()}:${new Date().getMinutes()}`, Validators.required],
  });

  acquisition = this.data.acquisition;
  session = this.data.session;
  containerFiles = new Set<string>();
  upload = new Subscription(); //kept separate from subscriptions object so it can continue uploading after the dialog closes
  uploadStatus: FileStatus[] = [];

  processingUploads = false;
  uploadComplete = false;
  uploadHasErrors = false;

  fileTypes: string[] = [];

  private subscriptions = {
    fileTypes: Subscription.EMPTY,
  }

  constructor(
    private config: ConfigService,
    @Inject(MAT_DIALOG_DATA) public data: AcquisitionUploadDialogData,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private uploadService: UploadService,
    public ref: MatDialogRef<AcquisitionUploadDialog, AcquisitionUploadDialogResult>,
    private fb: FormBuilder,
  ) {
    if (this.acquisition) {
      this.coreApi.containers
        .get<Container>(this.acquisition._id)
        .subscribe((container) => {
          this.containerFiles = new Set(
            container.files.map((file) => file.name)
          );
        });
    }
    this.subscriptions.fileTypes = this.config.getFileTypes().subscribe(fileTypes => {
      this.fileTypes = sortBy(Object.keys(fileTypes), fileType => fileType.toLowerCase());
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  getPendingUploads(): FileStatus[] {
    return this.uploadStatus.filter(
      (status) => status.progress < 100 && !status.error
    );
  }

  close(): void {
    const pendingUploads = this.getPendingUploads();
    if (pendingUploads.length > 0) {
      this.dialog.open(ChoiceDialog, {
        data: {
          content: `There are ${pendingUploads.length} pending uploads. Do you want to cancel them or continue in the background?`,
          choices: [
            { label: 'Cancel Uploads', value: 'cancel' },
            {
              label: 'Continue Uploads',
              value: 'continue',
              color: 'primary',
            },
          ],
        },
      }).afterClosed().subscribe((choice) => {
        if (choice === 'cancel') {
          this.upload.unsubscribe();
          this.ref.close(this.acquisition);
        } else if (choice === 'continue') {
          this.uploadPendingFiles();
          this.ref.close(this.acquisition);
        }
      });
    } else {
      this.ref.close(this.uploadComplete ? this.acquisition : null);
    }
  }

  uploadFileList(files: FileList): void {
    this.uploadComplete = false;
    for (const file of Array.from(files)) {
      const filesize = numbro(file.size ?? 0).format({
        output: 'byte',
        base: 'decimal',
        mantissa: 1,
        spaceSeparated: true,
      }).split(' ') as [string, string];
      const status: FileStatus = {
        file,
        size: filesize,
        progress: 0,
        subscription: Subscription.EMPTY,
        fileType: null,
      };
      this.uploadStatus.push(status);
      if (this.containerFiles.has(file.name)) {
        status.duplicate = true;
        status.error =
          'File already exists in container. Replace it with this new version?';
      } else {
        this.containerFiles.add(file.name);
      }
    }
    this.updateErrorStatus();
  }

  retryFile(status: FileStatus): void {
    status.duplicate = false;
    status.error = null;
    status.progress = 0;
    this.uploadStatus = this.uploadStatus.filter((other) => {
      // remove any competing duplicates from the list
      if (other.file.name === status.file.name && other !== status) {
        other.subscription.unsubscribe();
        return false;
      }
      return true;
    });

    // If we're in the middle of uploading, upload immediately
    if (this.processingUploads) {
      this.uploadFile(status);
    }
    this.updateErrorStatus();
  }

  uploadPendingFiles(): void {
    if (!this.acquisition?._id) {
      this.createAcquisition().subscribe({
        next: (acquisition) => {
          this.acquisition = acquisition;
          for (const status of this.uploadStatus) {
            this.uploadFile(status);
          }
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.notifications.error('acquisition.create.duplicate');
          } else {
            this.logger.error(error);
            this.notifications.error('acquisition.create.error');
          }
          for (const status of this.uploadStatus) {
            status.error = 'Unable to create a new acquisition, file not uploaded.';
          }
        },
      });
    } else {
      // Prevent re-upload if we're uploading more files after the first batch
      for (const status of this.uploadStatus.filter(
        (upload) => upload.progress < 100
      )) {
        this.uploadFile(status);
      }
    }
  }

  private uploadFile(status: FileStatus): void {
    status.subscription.unsubscribe();
    this.processingUploads = true;
    this.uploadComplete = false;
    status.subscription = this.uploadService
      .uploadFile(status, this.acquisition._id)
      .subscribe({
        next: (progress) => {
          status.progress = progress;
        },
        error: (error: HttpErrorResponse) => {
          this.notifications.error('container.file.upload.error');
          status.error = get(error, 'error.message', 'Unable to upload file.');
          this.updateErrorStatus();
        },
        complete: () => {
          this.notifications.success('container.file.upload.success');
          this.processingUploads = this.getPendingUploads().length > 0;
          this.uploadComplete = !this.processingUploads;
        },
      });
    this.upload.add(status.subscription);
  }

  createAcquisition(): Observable<Acquisition> {
    const acqTime = this.newAcqForm.controls.timestamp.value.split(':');
    const timestamp = new Date();
    timestamp.setHours(acqTime[0], acqTime[1]);
    const acquisition = {
      label: this.newAcqForm.controls.label.value,
      session: this.session._id,
      timestamp: timestamp.toISOString(),
    };
    return this.coreApi.acquisitions
      .post(acquisition)
      .pipe(
        switchMap(
          (acqPartial) =>
            this.coreApi.containers.get(
              acqPartial._id
            ) as Observable<Acquisition>
        )
      );
  }

  updateErrorStatus(): void {
    this.uploadHasErrors = this.uploadStatus.some((status) => status.error);
  }
}
